/**
 * IFRAP-AI.fr - Boot Screen Component
 * Complete system boot sequence with feature overview
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeatureIcon = ({ icon, className = "" }) => {
  const icons = {
    shield: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    terminal: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    database: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
      </svg>
    ),
    search: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    'chart-bar': (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    globe: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
    clock: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    plug: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    pen: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
      </svg>
    ),
    lock: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
    mail: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    circle: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <circle cx="12" cy="12" r="10" strokeWidth={2} />
      </svg>
    ),
  };
  
  return icons[icon] || icons.circle;
};

const BootScreen = ({ onComplete }) => {
  const { user } = useAuth();
  const [bootData, setBootData] = useState(null);
  const [bootPhase, setBootPhase] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBootData = async () => {
      try {
        const token = localStorage.getItem('ifrap_token');
        const response = await fetch(`${API}/system/boot`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setBootData(data);
        }
      } catch (err) {
        console.error('Boot sequence error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBootData();
  }, []);

  // Animate boot phases
  useEffect(() => {
    if (!bootData) return;
    
    const timer = setInterval(() => {
      setBootPhase(prev => {
        if (prev >= 4) {
          clearInterval(timer);
          return prev;
        }
        return prev + 1;
      });
    }, 800);

    return () => clearInterval(timer);
  }, [bootData]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#050505] flex items-center justify-center z-50">
        <div className="text-[#FFB000] font-mono text-xl animate-pulse">
          Initialisation système...
        </div>
      </div>
    );
  }

  if (!bootData) {
    onComplete?.();
    return null;
  }

  const { welcome, system } = bootData;

  return (
    <div className="fixed inset-0 bg-[#050505] z-50 overflow-auto">
      <div className="min-h-screen p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-[#FFB000] rounded-lg flex items-center justify-center">
              <span className="text-[#050505] font-bold text-2xl font-mono">IF</span>
            </div>
            <div className="text-left">
              <h1 className="text-3xl font-bold text-[#FFB000] font-['Space_Grotesk']">
                IFRAP-AI
              </h1>
              <p className="text-gray-500 font-mono text-sm">
                v{system.version} "{system.codename}"
              </p>
            </div>
          </div>
          
          {/* Welcome Message */}
          <div className={`mt-6 transition-opacity duration-500 ${bootPhase >= 0 ? 'opacity-100' : 'opacity-0'}`}>
            <p className="text-2xl text-[#33FF00] font-mono">
              {welcome.message}
            </p>
            <p className="text-gray-400 mt-1">
              Rang: <span className="text-[#FFB000]">{welcome.rank}</span>
            </p>
          </div>
        </div>

        {/* Boot Sequence */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Active Features */}
          <div className={`bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 transition-all duration-500 ${bootPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h2 className="text-[#33FF00] font-mono text-lg mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#33FF00] rounded-full animate-pulse"></span>
              Fonctionnalités Actives
            </h2>
            <div className="space-y-2">
              {system.features.active.map((feature, idx) => (
                <div key={feature.id} className="flex items-center gap-3 text-gray-300">
                  <FeatureIcon icon={feature.icon} className="w-4 h-4 text-[#33FF00]" />
                  <span className="font-mono text-sm">{feature.name}</span>
                </div>
              ))}
              {system.features.beta.map((feature, idx) => (
                <div key={feature.id} className="flex items-center gap-3 text-gray-400">
                  <FeatureIcon icon={feature.icon} className="w-4 h-4 text-[#FFB000]" />
                  <span className="font-mono text-sm">{feature.name}</span>
                  <span className="text-xs bg-[#FFB000]/20 text-[#FFB000] px-2 py-0.5 rounded">BETA</span>
                </div>
              ))}
            </div>
          </div>

          {/* Planned Features */}
          <div className={`bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 transition-all duration-500 ${bootPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h2 className="text-[#00FFFF] font-mono text-lg mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#00FFFF] rounded-full"></span>
              En Développement
            </h2>
            <div className="space-y-2">
              {system.features.planned.map((feature, idx) => (
                <div key={feature.id} className="flex items-center gap-3 text-gray-500">
                  <FeatureIcon icon={feature.icon} className="w-4 h-4 text-[#00FFFF]" />
                  <span className="font-mono text-sm">{feature.name}</span>
                  <span className="text-xs bg-[#00FFFF]/10 text-[#00FFFF] px-2 py-0.5 rounded">Phase 1c</span>
                </div>
              ))}
            </div>
          </div>

          {/* Active Sources */}
          <div className={`bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 transition-all duration-500 ${bootPhase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h2 className="text-[#FFB000] font-mono text-lg mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#FFB000] rounded-full"></span>
              Sources de Données
            </h2>
            <div className="space-y-2">
              {system.sources.active.map((source, idx) => (
                <div key={source.id} className="flex items-center gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-[#33FF00] rounded-full"></span>
                  <span className="font-mono text-sm">{source.name}</span>
                  <span className="text-xs text-gray-500">({source.type})</span>
                </div>
              ))}
              {system.sources.planned.map((source, idx) => (
                <div key={source.id} className="flex items-center gap-3 text-gray-500">
                  <span className="w-2 h-2 bg-gray-600 rounded-full"></span>
                  <span className="font-mono text-sm">{source.name}</span>
                  <span className="text-xs text-gray-600">À venir</span>
                </div>
              ))}
            </div>
          </div>

          {/* Developers */}
          <div className={`bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 transition-all duration-500 ${bootPhase >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="space-y-4">
              {/* Architecture & Dev */}
              <div>
                <h3 className="text-[#FFB000] font-mono text-sm mb-2">
                  {system.developers.architecture?.title}
                </h3>
                <div className="space-y-1">
                  {system.developers.architecture?.members?.map((member, idx) => (
                    <p key={idx} className="text-white text-sm">{member}</p>
                  ))}
                </div>
              </div>
              
              {/* Direction */}
              <div className="pt-3 border-t border-[#1a1a1a]">
                <h3 className="text-gray-500 text-xs mb-1">
                  {system.developers.direction?.title}
                </h3>
                <p className="text-white font-medium">{system.developers.direction?.name}</p>
                <p className="text-gray-400 text-sm">{system.developers.direction?.role}</p>
              </div>
              
              {/* Build Info */}
              <div className="pt-3 border-t border-[#1a1a1a]">
                <p className="text-xs text-gray-600 font-mono">
                  Développement démarré le {system.development_started}
                </p>
                <p className="text-xs text-gray-600 font-mono">
                  {system.total_requests} requêtes admin • Build #{system.build_number}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Version History */}
        <div className={`mt-6 bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4 transition-all duration-500 ${bootPhase >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h3 className="text-gray-400 font-mono text-sm mb-3">Historique des versions</h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {system.version_history?.slice().reverse().map((v, idx) => (
              <div key={idx} className="flex items-center gap-4 text-xs font-mono">
                <span className="text-[#FFB000] w-16">v{v.version}</span>
                <span className="text-gray-500 w-32">{v.date} {v.time}</span>
                <span className="text-gray-400">{v.name}</span>
                <span className="text-gray-600">Requête #{v.request_id}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Enter Button */}
        <div className={`text-center mt-8 transition-all duration-500 ${bootPhase >= 4 ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={onComplete}
            className="px-8 py-3 bg-[#FFB000] text-[#050505] font-mono font-bold rounded-lg hover:bg-[#FFB000]/90 transition-colors"
            data-testid="boot-enter-btn"
          >
            ENTRER DANS LE SYSTÈME
          </button>
          <p className="text-gray-600 text-sm mt-2 font-mono">
            Appuyez sur Entrée ou cliquez pour continuer
          </p>
        </div>
      </div>
    </div>
  );
};

export default BootScreen;
